import { createClient } from '@supabase/supabase-js'

// L'adresse du projet et la cle publique viennent des variables
// d'environnement, jamais du code. Voir .env.example.
//
// La cle "anon" est faite pour vivre dans le navigateur : ce sont les
// politiques RLS de schema.sql qui protegent les donnees. La cle
// "service_role" ne doit jamais approcher ce fichier.
const rawUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined
const rawKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

/**
 * Nettoie la cle.
 *
 * La cle voyage dans les en-tetes HTTP apikey et Authorization. Or un
 * en-tete HTTP n'accepte que des caracteres ISO-8859-1 : un seul
 * caractere invisible attrape en copiant-collant (guillemet courbe,
 * espace de largeur nulle, saut de ligne) fait echouer fetch() avant
 * meme qu'une requete ne parte, avec le message peu parlant
 * « String contains non ISO-8859-1 code point ».
 *
 * Une cle JWT ne contient que des caracteres base64url et des points.
 * Tout le reste est donc du bruit de collage, et on le retire.
 */
function cleanKey(value: string | undefined): string | undefined {
  if (!value) return undefined
  const cleaned = value.replace(/[^A-Za-z0-9._-]/g, '')
  return cleaned.length > 0 ? cleaned : undefined
}

const anonKey = cleanKey(rawKey)

/** Vraie si la cle fournie contenait des caracteres a retirer. */
export const keyWasCleaned = Boolean(rawKey) && rawKey!.trim() !== anonKey

if (keyWasCleaned) {
  console.warn(
    '[Escale] La cle VITE_SUPABASE_ANON_KEY contenait des caracteres ' +
      'invalides dans un en-tete HTTP ; ils ont ete retires. ' +
      'Recollez la cle proprement dans Netlify.',
  )
}

/**
 * Le client attend l'origine nue du projet, par exemple
 * https://abcdefgh.supabase.co
 *
 * Or l'ecran « API » de Supabase affiche aussi le point d'entree REST,
 * https://abcdefgh.supabase.co/rest/v1/ , et c'est celui-la que l'on
 * copie naturellement. Avec ce suffixe, toutes les requetes
 * d'authentification partent vers /rest/v1/auth/v1/... et repondent 404 :
 * l'application semble alors « ne pas avoir de connexion ».
 *
 * Plutot que d'echouer, on ramene l'adresse a son origine.
 */
function normaliseUrl(value: string | undefined): string | undefined {
  if (!value) return undefined
  // Meme nettoyage que pour la cle : on retire tout caractere
  // invisible attrape au collage avant d'analyser l'adresse.
  const trimmed = value.replace(/[^\x21-\x7E]/g, '')
  // Une adresse collee sans "https://" est acceptee aussi.
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  try {
    // Ne garde que le schema et le domaine, en jetant tout chemin.
    return new URL(withScheme).origin
  } catch {
    return undefined
  }
}

const url = normaliseUrl(rawUrl)

export const isConfigured = Boolean(url && anonKey)

/** Vraie si l'adresse fournie portait un chemin qu'il a fallu retirer. */
export const urlWasCorrected =
  Boolean(rawUrl && url) && rawUrl!.trim().replace(/\/+$/, '') !== url

/**
 * Faits verifiables sur la configuration reellement embarquee dans le
 * site publie. Affiches, replies, sur l'ecran de connexion.
 *
 * Pourquoi : les valeurs sont saisies dans le tableau de bord Netlify
 * puis figees dans le JavaScript a la construction. Quand elles sont
 * fausses, le symptome est un message d'erreur du serveur qui ne dit
 * rien de la cause. Mieux vaut pouvoir lire, depuis le telephone, ce
 * que le site utilise vraiment.
 *
 * La cle anon est publique par nature, mais on n'en montre que les
 * extremites : cela suffit a reperer une troncature.
 */
function refFromKey(key: string | undefined): string | null {
  if (!key) return null
  try {
    const payload = JSON.parse(atob(key.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    return payload.ref ?? null
  } catch {
    return null
  }
}

function refFromUrl(origin: string | undefined): string | null {
  if (!origin) return null
  try {
    const host = new URL(origin).hostname
    return host.endsWith('.supabase.co') ? host.split('.')[0] : null
  } catch {
    return null
  }
}

const keyRef = refFromKey(anonKey)
const urlRef = refFromUrl(url)

export const configFacts = {
  urlInUse: url ?? '(vide)',
  keyLength: anonKey?.length ?? 0,
  keyHead: anonKey ? anonKey.slice(0, 8) : '',
  keyTail: anonKey ? anonKey.slice(-8) : '',
  keyWasCleaned,
  urlWasCorrected,
  keyRef: keyRef ?? '(illisible)',
  urlRef: urlRef ?? '(illisible)',
  /** Le plus revelateur : la cle appartient-elle bien a ce projet ? */
  refsMatch: Boolean(keyRef && urlRef && keyRef === urlRef),
}

export const supabase = createClient(url ?? 'http://localhost', anonKey ?? 'missing', {
  auth: {
    // La session survit a la fermeture de l'application : Idrissa ne
    // doit pas redemander un lien par email chaque matin.
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
})
